import React, { useState } from 'react';
import DropZone from 'react-dropzone';
import DrawerV6 from '@Common/Drawer/DrawerV6/DrawerV6';
import FileChooser from '@Common/FileChooser';
import Icon from '@Common/Icon';
import Notification from '@Common/Notification';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import ImportErrorRow from '@Common/ImportErrorRow';
import Button from '@Common/Button';
import * as types from '@Constants/actionTypes';
import { LocationTaskImportError } from '@Types/common';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import './LocationTaskImportDrawer.scss';

interface LocationTaskImportDrawerProps {
    subscriptionId: string;
    locationKey: string;
    locationName: string;
    onComplete: () => void;
    onCancel: () => void;
    locationTaskActions: typeof locationTaskActions;
}

const LocationTaskImportDrawer: React.FC<LocationTaskImportDrawerProps> = ({
    subscriptionId,
    locationKey,
    locationName,
    onComplete,
    onCancel,
    locationTaskActions
}) => {
    const [droppedFile, setDroppedFile] = useState<File | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isFormValid, setIsFormValid] = useState<boolean>(true);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [importErrors, setImportErrors] = useState<LocationTaskImportError[]>([]);
    const [fileInputTarget, setFileInputTarget] = useState<HTMLInputElement | null>(null);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setDroppedFile(null);
            setIsFormValid(true);
            setImportErrors([]);
            setFileInputTarget(e.target);
        }
    };

    const onDrop = (files: File[]): void => {
        const droppedFile = files && files.length > 0 ? files[0] : null;

        if (droppedFile) {
            if (droppedFile.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                Notification("error", "Invalid File Type", "You must pick an Excel document with .xlsx extension");
                setDroppedFile(null);
                setSelectedFile(null);
                setIsFormValid(false);
            } else {
                setDroppedFile(droppedFile);
                setSelectedFile(null);
                setIsFormValid(true);
                setImportErrors([]);
            }
        }
    };

    const onImportClick = (): void => {
        if (droppedFile) {
            doImport(droppedFile);
        } else if (selectedFile) {
            doImport(selectedFile);
        } else {
            setIsFormValid(false);
        }
    };

    const doImport = async (importFile: File): Promise<void> => {
        setIsImporting(true);
        setImportErrors([]);

        try {
            const response: any = await locationTaskActions.import(
                subscriptionId,
                locationKey,
                importFile
            );

            if (response.type === types.IMPORT_LOCATION_TASK_SUCCESS) {
                if (response.payload.importErrors && response.payload.importErrors.length > 0) {
                    Notification(
                        "error",
                        "Validation Errors",
                        "Please correct the errors in the Excel document and try again"
                    );
                    
                    // Transform API errors to match frontend interface
                    const transformedErrors = response.payload.importErrors.map((apiError: any) => ({
                        rowNumber: apiError.row || apiError.rowNumber,
                        columnNumber: apiError.column || apiError.columnNumber || 1, // Default to column 1 if not provided
                        errorMessage: apiError.error || apiError.errorMessage || 'Unknown error',
                        locationName: apiError.locationName,
                        taskName: apiError.taskName
                    }));
                    
                    setImportErrors(transformedErrors);
                    setIsImporting(false);
                    setSelectedFile(null);
                    setDroppedFile(null);

                    // Clear the file input
                    if (fileInputTarget) {
                        fileInputTarget.value = '';
                    }
                } else {
                    const successCount = response.payload.successfulImports || response.payload.importedCount || 0;
                    Notification(
                        "success",
                        "Import Successful",
                        `Successfully imported ${successCount} location task${successCount !== 1 ? 's' : ''}`
                    );
                    setIsImporting(false);
                    onComplete();
                }
            } else if (response.type === types.IMPORT_LOCATION_TASK_FAILURE) {
                Notification("error", "Error", response.payload.message);
                setIsImporting(false);
            }
        } catch (error) {
            Notification("error", "Error", error instanceof Error ? error.message : String(error));
            setIsImporting(false);
        }
    };

    const onDownload = async (): Promise<void> => {
        const error = await locationTaskActions.importTemplate(locationKey);
        if (error) {
            Notification("error", "", error);
        }
    };

    return (
        <div>
            <DrawerV6
                isWide={true}
                title={`Location Task Import - ${locationName}`}
                onCancelClick={onCancel}
                saveText="Import"
                onSaveClick={onImportClick}
                isSaveDisabled={isImporting}
            >
                <LoadingIndicator isLoading={isImporting}>
                    <div className="location-task-import-content">
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="alert alert-danger" style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb', color: '#721c24' }}>
                                    <strong>Important:</strong> These tasks are unique to this location. To apply tasks to a baseline or multiple locations, click cancel and go to the Project Control Hub.
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-1">
                                <h1><span className="pacer-step-badge">1</span></h1>
                            </div>
                            <div className="col-9">
                                <div className="mt-3">
                                    Download the location task import Excel template
                                </div>
                            </div>
                            <div className="col-2">
                                <div className="mt-1">
                                    <Button
                                        type="btn"
                                        bootstrapClass="primary text-end"
                                        onClick={onDownload}
                                        text="Download"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-1">
                                <h1><span className="pacer-step-badge">2</span></h1>
                            </div>
                            <div className="col-11">
                                <div className="mt-2">
                                    Add your location tasks to the Excel template. Ensure all required fields
                                    are entered for each task. If you copy/paste from another workbook,
                                    please use Paste Special &gt; Values.
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-1">
                                <h1 className="mt-2"><span className="pacer-step-badge">3</span></h1>
                            </div>
                            <div className="col-11">
                                <DropZone
                                    noClick={true}
                                    accept={{
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                                    }}
                                    multiple={false}
                                    onDrop={onDrop}
                                >
                                    {({ getRootProps, getInputProps, isDragActive }) => (
                                        <div {...getRootProps({
                                            className: `dropzone ${isDragActive ? 'active' : ''}`
                                        })}>
                                            <input {...getInputProps()} />
                                            {isDragActive ? (
                                                <div className="dropzone-overlay">
                                                    <div className="overlay-content">
                                                        <Icon name="cloud-upload-alt" />
                                                        <p>Drop file here to import</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p>Drag and drop a file here or select one below</p>
                                                    <FileChooser
                                                        name="file"
                                                        onChange={onFileChange}
                                                        isRequired
                                                        errorMessage="A file is required"
                                                        acceptTypes=".xlsx"
                                                    />
                                                    {droppedFile && (
                                                        <div className="dropped-file">
                                                            File Name: {droppedFile.name}
                                                        </div>
                                                    )}
                                                    {selectedFile && (
                                                        <div className="selected-file">
                                                            Selected File: {selectedFile.name}
                                                        </div>
                                                    )}
                                                    {!isFormValid && (
                                                        <div className="text-danger">
                                                            You must select or drag and drop a valid excel file
                                                        </div>
                                                    )}
                                                    {importErrors.length > 0 && (
                                                        <>
                                                            <div className="text-danger">
                                                                {importErrors.length} error
                                                                {importErrors.length === 1 ? '' : 's'} found during the import:
                                                            </div>
                                                            {importErrors.map((importError, index) => (
                                                                <ImportErrorRow
                                                                    importError={importError}
                                                                    key={`${importError.rowNumber}-${importError.columnNumber}-${index}`}
                                                                />
                                                            ))}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </DropZone>
                            </div>
                        </div>
                    </div>
                </LoadingIndicator>
            </DrawerV6>
        </div>
    );
};

export default LocationTaskImportDrawer;